import L from 'leaflet';
import React, { useEffect, useState } from 'react';
import { MapContainer, Marker, Polyline, TileLayer } from 'react-leaflet';
import { useNavigate, useParams } from 'react-router-dom';
import { RouteFullModal, RouteOverlay } from '../../components/maps/RouteOverlay';
import BottomNavbar from '../../layouts/BottomNavbar';
import TopBar from '../../layouts/TopBar';
import MenuSidebar from '../../layouts/MenuSidebar';
import { darkTheme, lightTheme } from '../../theme/theme';
import { getActiveProduct, getProductDataFolder, PRODUCTS } from '../../utils/urlConfig';
import useTheme from '../../hooks/useTheme';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const cartoKey = process.env.REACT_APP_CARTO_KEY;

const MapScreen = () => {
    const [isDark, setIsDark] = useTheme();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isRouteModalOpen, setIsRouteModalOpen] = useState(false);
    const [routePoints, setRoutePoints] = useState([]);
    const [activeProduct, setActiveProductState] = useState('');
    const navigate = useNavigate();
    const { productId: urlProductId, serialNumber: urlSerialNumber } = useParams();

    const openRouteModal = () => {
        setIsRouteModalOpen(true);
        window.history.pushState({ mapModal: true }, '');
    };

    useEffect(() => {
        const handlePopState = () => {
            if (isRouteModalOpen) {
                setIsRouteModalOpen(false);
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [isRouteModalOpen]);

    const theme = isDark ? darkTheme : lightTheme;
    
    useEffect(() => {
        if (urlProductId && urlSerialNumber) {
            let product = getActiveProduct();
            for (const [key, config] of Object.entries(PRODUCTS)) {
                if (config.gtin === urlProductId) {
                    product = key;
                    break;
                }
            }
            setActiveProductState(product);
        } else {
            setActiveProductState(getActiveProduct());
        }
    }, [urlProductId, urlSerialNumber]);

    useEffect(() => {
        const loadRouteData = async () => {
            try {
                const dataFolder = getProductDataFolder(activeProduct);
                const data = await import(`../../data/${dataFolder}/map_route.json`);
                setRoutePoints(data.default || data);
            } catch (error) {
                console.error('Error loading route data:', error);
                setRoutePoints([]);
            }
        };
        loadRouteData();
    }, [activeProduct]);

    // Custom Icon Creator
    const createCustomIcon = (label) => {
        return L.divIcon({
            className: 'custom-marker-icon', // Styles defined in index.css
            html: `<span>${label}</span>`,
            iconSize: [24, 24],
            iconAnchor: [12, 12],  
        });
    };

    return (
        <div
            className="flex flex-col h-screen w-full relative overflow-hidden"
            style={{ backgroundColor: theme.background1 }}
        >
            {/* Header */}
            <TopBar
                theme={theme}
                isDark={isDark}
                setIsDark={setIsDark}
                title="Map"
                onBackClick={() => {
                    const backPath = urlProductId && urlSerialNumber 
                        ? `/01/${urlProductId}/21/${urlSerialNumber}/dpp`
                        : `/01/${PRODUCTS[activeProduct]?.gtin}/21/${PRODUCTS[activeProduct]?.serialNumber}/dpp`;
                    navigate(backPath, { state: { isDark } });
                }}
                isMenuOpen={isMenuOpen}
                setIsMenuOpen={setIsMenuOpen}
                fixed={false}
            />

            {/* Menu Sidebar */}
            <MenuSidebar
                theme={theme}
                isDark={isDark}
                isOpen={isMenuOpen}
                onClose={() => setIsMenuOpen(false)}
            />

            {/* Map */}
            <div className="flex-1 w-full relative z-0">
                {routePoints.length === 0 && <LoadingSpinner overlay />}
                <MapContainer
                    id="map"
                    center={[30.0, 5.0]}
                    zoom={3}
                    style={{ height: '100%', width: '100%' }}
                    zoomControl={true}
                >
                    <TileLayer
                        attribution='&copy; OpenStreetMap contributors &copy; CARTO'
                        url={`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${cartoKey}`}
                    />

                    {routePoints.map((point, index) => (
                        <React.Fragment key={point.id}>
                            <Marker
                                position={point.position}
                                icon={createCustomIcon(point.label)}
                            />
                            {index < routePoints.length - 1 && (
                                <Polyline
                                    positions={[point.position, routePoints[index + 1].position]}
                                    pathOptions={{ color: point.pathColor || 'red', weight: 3 }}
                                />
                            )}
                        </React.Fragment>
                    ))}
                </MapContainer>

                {/* Route Overlay Card */}
                {!isRouteModalOpen && (
                <div className="fixed bottom-[100px] left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] z-[400] md:bottom-4">
                     <RouteOverlay 
                        stops={routePoints} 
                        isDark={isDark} 
                        onViewFull={() => openRouteModal()}
                     />
                </div>
                )}
            </div>

            <RouteFullModal 
                stops={routePoints} 
                isOpen={isRouteModalOpen} 
                onClose={() => window.history.back()} 
                isDark={isDark} 
            />

            {/* Spacer for navigation */}
            {!isRouteModalOpen && (
                <>
                <div className="flex-none h-[75px]" style={{ backgroundColor: theme.background1 }}></div>

                {/* Bottom Navigation */}
                <div className="absolute bottom-0 w-full z-50">
                    <BottomNavbar theme={theme} isDark={isDark} />
                </div>
                </>
            )}

        </div>
    );
};

export default MapScreen;